// src/features/collaboration/components/PostCreationInput.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Avatar, 
  Typography, 
  Paper,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Image as ImageIcon,
  MenuBook as ResearchIcon,
  ArticleOutlined as ArticleIcon
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { THEME_COLORS } from '../../../styles/theme';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

  const PostCreationInput = ({ onCreatePost }) => {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Fetch user profile from Firestore
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        const userProfileDoc = await getDoc(doc(db, "userProfiles", currentUser.uid));
        
        if (userProfileDoc.exists()) {
          setUserProfile({
            id: userProfileDoc.id,
            ...userProfileDoc.data()
          });
        } else {
          console.log("No user profile found in Firestore");
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [currentUser]);
  
  // Safe access to profile data
  const userName = userProfile?.firstName || (currentUser?.displayName || 'User');
  const userInitial = userName ? userName.charAt(0) : '?';
  const userImage = userProfile?.profileImage || null;
  
  if (loading) {
    return (
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} sx={{ color: THEME_COLORS.primary }} />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card sx={{ 
      mb: 0, 
      borderRadius: 2, 
      bgColor: '#f6f5f5',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      overflow: 'visible'
    }}>
      <CardContent sx={{ pt: 2 }}>
        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
          <Avatar 
            src={userImage} 
            alt={userName}
            sx={{ 
              width: 48, 
              height: 48, 
              mr: 2,
              bgcolor: THEME_COLORS.primary,
              border: '2px solid #fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            {userInitial}
          </Avatar>
          
          <Paper
            onClick={() => onCreatePost?.('post')}
            sx={{
              flex: 1,
              p: 1.0,
              pl: 2,
              backgroundColor: '#f6f5f5',
              borderRadius: '24px',
              cursor: 'pointer',
              border: '1px solid #e0e0e0',
              '&:hover': {
                backgroundColor: '#f9f9f9',
                borderColor: '#272361'
              }
            }}
          >
            <Typography color="textSecondary">
              Start a post
            </Typography>
          </Paper>
        </Box>
        
        <Divider sx={{ mb: 0 }} />
        
        <Box display="flex" justifyContent="space-between">
          <Button 
            startIcon={<ImageIcon style={{ color: '#4CAF50' }} />}
            onClick={() => onCreatePost?.('post')}
            sx={{ 
              textTransform: 'none', 
              borderRadius: 2,
              flex: 1,
              mr: 1
            }}
          >
            Media
          </Button>
          
          <Button 
            startIcon={<ResearchIcon style={{ color: '#E91E63' }} />}
            onClick={() => onCreatePost?.('research')}
            sx={{ 
              textTransform: 'none', 
              borderRadius: 2,
              flex: 1,
              mr: 1
            }}
          >
            Deep Research
          </Button>
          
          <Button 
            startIcon={<ArticleIcon style={{ color: '#2196F3' }} />}
            onClick={() => onCreatePost?.('article')}
            sx={{ 
              textTransform: 'none', 
              borderRadius: 2,
              flex: 1
            }}
          >
            Write article
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PostCreationInput;
